import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "../lib/brand";

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleNav = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-navy/95 backdrop-blur-md shadow-[0_1px_0_rgba(201,168,76,0.2)]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <button
                        onClick={() => handleNav("inicio")}
                        data-testid="navbar-logo"
                        className="flex items-center gap-3 group"
                        aria-label="Ir al inicio"
                    >
                        <img
                            src={BRAND.logoMarkGold}
                            alt="Logo Dr. Erik E. Cárdenas Hernández"
                            className="h-14 w-14 lg:h-16 lg:w-16 object-contain"
                        />
                        <div className="hidden md:flex flex-col leading-tight text-left">
                            <span className="font-display text-base lg:text-lg font-semibold tracking-tight text-cream">
                                Dr. Erik E. Cárdenas Hernández
                            </span>
                            <span className="text-[9px] tracking-[0.28em] uppercase font-medium text-gold mt-0.5">
                                YFACS · MASPEN
                            </span>
                        </div>
                    </button>

                    {/* Desktop nav */}
                    <nav className="hidden lg:flex items-center gap-9">
                        {NAV_LINKS.map((link) => (
                            <button
                                key={link.id}
                                data-testid={`nav-link-${link.id}`}
                                onClick={() => handleNav(link.id)}
                                className="text-sm tracking-wide text-cream/85 hover:text-gold transition-colors duration-300 relative group"
                            >
                                {link.label}
                                <span className="absolute -bottom-1.5 left-0 right-0 h-px bg-gold scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
                            </button>
                        ))}
                    </nav>

                    {/* CTA */}
                    <div className="hidden lg:block">
                        <button
                            data-testid="navbar-cta-button"
                            onClick={() => handleNav("contacto")}
                            className="px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold border border-gold text-gold hover:bg-gold hover:text-navy transition-all duration-300"
                        >
                            Agendar consulta
                        </button>
                    </div>

                    {/* Mobile menu button */}
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((s) => !s)}
                        className="lg:hidden text-cream"
                        aria-label="Abrir menú"
                    >
                        {open ? <X size={26} /> : <Menu size={26} />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            <div
                className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 bg-navy ${
                    open ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                <nav className="px-6 py-6 flex flex-col gap-1 border-t border-gold/20">
                    {NAV_LINKS.map((link) => (
                        <button
                            key={link.id}
                            data-testid={`mobile-nav-link-${link.id}`}
                            onClick={() => handleNav(link.id)}
                            className="text-left py-3 text-cream/90 hover:text-gold transition-colors text-base font-light tracking-wide"
                        >
                            {link.label}
                        </button>
                    ))}
                    <button
                        data-testid="mobile-cta-button"
                        onClick={() => handleNav("contacto")}
                        className="mt-4 px-5 py-3 text-xs tracking-[0.2em] uppercase font-semibold border border-gold text-gold hover:bg-gold hover:text-navy transition-all"
                    >
                        Agendar consulta
                    </button>
                </nav>
            </div>
        </header>
    );
};

export default Navbar;
